import { Location } from '@angular/common';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { RoutePath } from '@app/models-internal/route-path';
import { BehaviorSubject } from 'rxjs';

//import * as UrlParse from '../../../node_modules/url-parse';
import { DateService, LogService, ValueService } from '@oa/shared-core';

/**
 * Route Service Helper
 * Notable methods:
 *    1. Open Link in New tab (openRouteInNewTab)
 *    2. Gets single parameter from a route (getRouteParameter)
 *    3. Gets a query string value from a route (getQueryStringValue)
 */
@Injectable({
  providedIn: "root",
})
export class RouteHelperService {
  // check the second one
  private readonly _serviceName = 'RouteHelperService';
  private routePaths = [{ code: 'ld', path: '/listing/:id' } as RoutePath];

  public host: string;

  /**
   * Serves as a workaround to the guard-urltree navigation extras issue: https://github.com/angular/angular/issues/27148
   */
  public resourceUnavailableState = new BehaviorSubject<any>(null);

  public navigationHistory = [];
  private routerEventsSubscription: any;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private valueHelper: ValueService,
    private route: ActivatedRoute,
    private router: Router,
    private log: LogService,
    private dateHelper: DateService,
    private _location: Location
  ) {
    this.host = this.document.location.origin;

    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        //do something on end activity

        this.navigationHistory.push({
          timestamp: this.dateHelper.nowUTC,
          url: event.url,
        });

        // console.log(`${this._serviceName}.CTOR - router.events - navigationHistory=`, this.navigationHistory);
      }
    });
  }

  /**
   * Adds a query string parameter to the route but DOES NOT reload the route
   * @param route
   * @param key
   * @param id
   */
  public appendQueryStringParameter(route: ActivatedRoute, key: string, id: any) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [key]: id },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  /**
   * Parses a url and returns back an object with the url components. This function wraps url-parse.  See https://www.npmjs.com/package/url-parse
   *
   * @param url
   * @returns
   * protocol: The protocol scheme of the URL (e.g. http:).
   * slashes: A boolean which indicates whether the protocol is followed by two forward slashes (//).
   * auth: Authentication information portion (e.g. username:password).
   * username: Username of basic authentication.
   * password: Password of basic authentication.
   * host: Host name with port number.
   * hostname: Host name without port number.
   * port: Optional port number.
   * pathname: URL path.
   * query: Parsed object containing query string, unless parsing is set to false.
   * hash: The "fragment" portion of the URL including the pound-sign (#).
   * href: The full URL.
   * origin: The origin of the URL.
   */
  // public parseUrl(url: string): any {
  //   var url = new UrlParse(url);

  //   return url as any;
  // }

  /**
   * Gets the route path for a route code.
   * @param routeCode
   */
  getRoutePath(routeCode: any): RoutePath {
    if (!routeCode) {
      return null;
    }

    let routePath = this.routePaths.find((x) => x.code === routeCode);

    return routePath;
  }

  public get hasPostDialogRedirect(): boolean {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.hasPostDialogRedirect`);
    }

    let b = false;

    if (this.postDialogRedirect) {
      b = true;
    }

    return b;
  }

  public get postDialogRedirect(): string {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.postDialogRedirect`);
    }
    let v: string = (window as any).oa.postDialogRedirect;

    return v;
  }

  /**
   * Wrapper method around the getting a query string parameter value from the current route
   * - currently uses route.snapshot
   * @param key
   */
  public getQueryStringValue(key: string) {
    return this.route.snapshot.queryParamMap.get(key);
  }

  /**
   * Parses a listing id from the s/l/l- url path
   */
  public getRedirectListingDetailListingId() {
    // we have 2 patterns for listing details
    // First - s/l/l- which is the static pages used for sharing links to Facebook.
    // Second -  the typical angular route #/listing/{listingId}

    const pattern = 's/l/l-';
    const v: string = (window as any).oa.postDialogRedirect;

    if (!v) {
      return null;
    }

    let i = v.indexOf(pattern);

    if (i > 0) {
      const x = i + pattern.length;

      const v2 = v.substring(x);

      const y = v2.indexOf('.');

      if (y > 0) {
        const v3 = v2.substring(0, y);

        if (this.valueHelper.isNumeric(v3)) {
          return +v3;
        }
      }
    }

    // if none was found for the first patter,
    // check the second one
    const listingDetailPattern = '#/listing/';
    i = v.indexOf(listingDetailPattern);

    if (i > 0) {
      const x = i + listingDetailPattern.length;
      const v2 = v.substring(x);

      if (v2) {
        return +v2;
      }
    }

    // return null if no matches found
    return null;
  }

  /**
   * Gets the Landowner share agreement Url
   * @param secureId
   * @returns
   */
  public getLandownerShareAgreementUrl(secureId: string): string {
    let url = `${this.host}/#/agreement/${secureId}`;

    return url;
  }

  /**
   * Builds a path for a listing id to the listing details route
   */
  public buildListingDetailPath(listingId: number) {
    return `/listing/${listingId}`;
  }

  public isRedirectSourceStaticListing(): boolean {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.isRedirectSourceStaticListing`);
    }

    let v: string = (window as any).oa.postDialogRedirect;

    let b = false;
    let reason = 'Initial False Condition';

    if (!this.hasPostDialogRedirect) {
      reason = "There is no 'oa.postDialogRedirect' value.";
    } else {
      if (!this.getRedirectListingDetailListingId()) {
        reason = `No value could be parsed from: '${v}'`;
      } else {
        b = true;
        reason = 'Value exists';
      }
    }

    console.log(`${this._serviceName}.isRedirectSourceStaticListing returns: '${b}'. Reason: ${reason}`);
    return b;
  }

  public reloadCurrentRoute(router: Router) {
    let currentUrl = router.url;
    router.navigateByUrl('/', { skipLocationChange: false }).then(() => {
      router.navigate([currentUrl]);
    });
  }

  /**
   * Gets a route parameter
   *  - uses route.snapshop if a subscription is needed, use:
            this.route.paramMap.subscribe( paramMap => {
                this.val = paramMap.get('name');
            })
   * @param name
   */
  public getRouteParameter(r: ActivatedRoute, name: string): any {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.getRouteParameter() name=${name},`, r.snapshot.paramMap);
    }
    let value = r.snapshot.paramMap.get(name);

    if (!value && r.parent) {
      value = r.parent.snapshot.paramMap.get(name);
    }

    return value;
  }

  public hasUrlSegment(r: ActivatedRoute, urlSegment: string): boolean {
    if (r && r.snapshot && r.snapshot.children && r.snapshot.children[0] && r.snapshot.children[0].url) {
      return r.snapshot.children[0].url.some((x) => this.valueHelper.equalsIgnoringCase(x.path, urlSegment));
    } else {
      return false;
    }
  }

  public goHome(externalOrgCode?: string) {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.goHome externalOrgCode=${externalOrgCode}`);
    }

    let path = '/';

    if (externalOrgCode) {
      path = `/eoh/${externalOrgCode}`;
    }

    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.goHome externalOrgCode=${externalOrgCode}, path=${path}`);
    }

    this.router.navigate([path], { queryParamsHandling: 'merge' });
  }

  /**
   * Opens the route path in a new tab
   * @param routePath
   */
  public openRouteInNewTab(routePath: string) {
    if (!routePath) {
      throw new Error("'routePath' parameter is empty.");
    }

    let url = `${this.host}/#${routePath}`;

    window.open(url, '_blank');
  }

  public getPreviewShareUrl(listingId: number, shareId: string) {
    let url = `${this.host}/listing/${listingId}?previewShareId=${shareId}`;
    return url;
  }

  /**
   * Parses an Angular route path from a url.
   * @param url
   */
  public parseRoutePath(url: string, ignoreQueryString: boolean) {
    if (ignoreQueryString) {
      url = url.split('?')[0];
    }

    let urlObject = new URL(url);

    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.parseRoutePath - urlObject=`, urlObject);
    }

    const angularRoutePattern = `${urlObject.origin}/#`;

    if (url.startsWith(angularRoutePattern)) {
      return url.replace(angularRoutePattern, '');
    }

    return null;
  }

  /**
   * Gets the a state value from a route
   * @param router
   * @param propertyName
   */
  public getStateValue(router: Router, propertyName: string): any {
    const n = this.router.currentNavigation();

    return this.valueHelper.getSafeValue(n, `extras.state.${propertyName}`);
  }

  /**
   * Navigates to a route that is one level up from the current route path (i.e. ..)
   * @param route
   */
  public navigateUpOneLevel(route: ActivatedRoute) {
    this.router.navigate(['..'], {
      relativeTo: route,
    });
  }

  returnPreviousUrl() {
    this._location.back();
  }
}
